import type { TickerMatch, WidgetConfig } from "./types";
import { strings, formatKickoff } from "./i18n";

/** Build a flag <img>, or null when no flag URL is known. */
function flagImg(url: string, code: string): HTMLImageElement | null {
  if (!url) return null;
  const img = document.createElement("img");
  img.className = "wc-flag";
  img.src = url;
  img.alt = code;
  img.loading = "lazy";
  img.decoding = "async";
  // If the CDN flag fails, hide the broken image rather than show it.
  img.addEventListener("error", () => {
    img.style.display = "none";
  });
  return img;
}

function side(code: string, flag: string, flagFirst: boolean): HTMLElement {
  const el = document.createElement("span");
  el.className = "wc-side";
  const codeEl = document.createElement("span");
  codeEl.className = "wc-code";
  codeEl.textContent = code;
  const img = flagImg(flag, code);
  if (flagFirst) {
    if (img) el.appendChild(img);
    el.appendChild(codeEl);
  } else {
    el.appendChild(codeEl);
    if (img) el.appendChild(img);
  }
  return el;
}

function stateEl(m: TickerMatch, lang: string): HTMLElement {
  const str = strings(lang);
  const el = document.createElement("span");
  el.className = "wc-state";
  if (m.status === "UPCOMING") {
    const k = document.createElement("span");
    k.className = "wc-kickoff";
    k.textContent = m.startsAt ? formatKickoff(m.startsAt, lang) : "";
    el.appendChild(k);
    el.setAttribute("aria-label", k.textContent);
  } else if (m.status === "LIVE") {
    const dot = document.createElement("span");
    dot.className = "wc-dot";
    dot.setAttribute("aria-hidden", "true");
    const min = document.createElement("span");
    min.className = "wc-min";
    // "~" marks an estimated minute (inferred from kickoff, no live feed).
    const tilde = m.estimated ? "~" : "";
    min.textContent = m.minute != null ? `${tilde}${m.minute}'` : str.live;
    if (m.estimated) min.title = "minuto estimado";
    el.append(dot, min);
    el.setAttribute("aria-label", `${str.live} ${m.minute ?? ""}`.trim());
  } else if (m.status === "HT") {
    const ht = document.createElement("span");
    ht.className = "wc-htlabel";
    ht.textContent = "HT";
    el.appendChild(ht);
    el.setAttribute("aria-label", str.ht);
  } else {
    const ft = document.createElement("span");
    ft.className = "wc-ft";
    ft.textContent = "FT";
    el.appendChild(ft);
    el.setAttribute("aria-label", str.ft);
  }
  return el;
}

/** Build a single match card. */
export function createCard(m: TickerMatch, cfg: WidgetConfig): HTMLElement {
  const statusClass =
    m.status === "LIVE" || m.status === "HT"
      ? " wc-live" // in-play (HT is paused, still "live")
      : m.status === "UPCOMING"
      ? " wc-upcoming"
      : " wc-ft";
  const card = document.createElement("div");
  card.className = "wc-card" + statusClass;
  card.setAttribute("role", "listitem");
  card.dataset.id = m.id;
  if (cfg.expandable) card.setAttribute("data-clickable", "");

  const label =
    m.status === "UPCOMING"
      ? `${m.home.name} vs ${m.away.name}`
      : `${m.home.name} ${m.score.home}-${m.score.away} ${m.away.name}`;
  card.setAttribute("aria-label", label);

  if (m.stage) {
    const stage = document.createElement("span");
    stage.className = "wc-stage";
    stage.textContent = m.stage;
    card.appendChild(stage);
  }

  // Center: score for played/in-play matches, a neutral "vs" for upcoming.
  const center = document.createElement("span");
  center.className = "wc-score";
  center.textContent =
    m.status === "UPCOMING" ? "vs" : `${m.score.home} - ${m.score.away}`;

  card.appendChild(side(m.home.code, m.home.flag, true));
  card.appendChild(center);
  card.appendChild(side(m.away.code, m.away.flag, false));
  card.appendChild(stateEl(m, cfg.lang));

  return card;
}

/** The static shell of the bar; cards are (re)filled by `fillCards`. */
export interface BarRefs {
  /** Title + logo strip above the bar. */
  header: HTMLElement;
  bar: HTMLElement;
  /** Fixed area holding live/HT cards (does not scroll). */
  pinned: HTMLElement;
  /** Scrolling area holding upcoming + finished cards. */
  scroller: HTMLElement;
  prev: HTMLElement;
  next: HTMLElement;
  detail: HTMLElement;
  updating: HTMLElement;
}

export function buildBar(cfg: WidgetConfig): BarRefs {
  const str = strings(cfg.lang);

  // Header: logo + title above the matches.
  const header = document.createElement("div");
  header.className = "wc-header";
  if (cfg.logo) {
    const img = document.createElement("img");
    img.className = "wc-logo";
    img.src = cfg.logo;
    img.alt = "";
    img.loading = "lazy";
    img.decoding = "async";
    img.addEventListener("error", () => {
      img.style.display = "none";
    });
    header.appendChild(img);
  } else {
    const emoji = document.createElement("span");
    emoji.className = "wc-logo-emoji";
    emoji.textContent = "🏆";
    emoji.setAttribute("aria-hidden", "true");
    header.appendChild(emoji);
  }
  const title = document.createElement("span");
  title.className = "wc-title";
  title.textContent = cfg.title || str.title;
  header.appendChild(title);

  const bar = document.createElement("div");
  bar.className = "wc-bar";

  // Pinned live cards stay put on the left.
  const pinned = document.createElement("div");
  pinned.className = "wc-pinned";
  pinned.setAttribute("role", "list");
  pinned.setAttribute("aria-label", str.live);

  // Everything else scrolls inside this wrapper (which scopes the arrows).
  const wrap = document.createElement("div");
  wrap.className = "wc-scroll-wrap";

  const prev = document.createElement("button");
  prev.className = "wc-arrow wc-prev";
  prev.type = "button";
  prev.setAttribute("aria-label", str.prev);
  prev.textContent = "‹";

  const next = document.createElement("button");
  next.className = "wc-arrow wc-next";
  next.type = "button";
  next.setAttribute("aria-label", str.next);
  next.textContent = "›";

  const scroller = document.createElement("div");
  scroller.className = "wc-scroller";
  scroller.setAttribute("role", "list");
  scroller.setAttribute("aria-label", str.ariaRegion);

  wrap.append(prev, scroller, next);

  const updating = document.createElement("span");
  updating.className = "wc-updating";
  updating.setAttribute("aria-hidden", "true");

  bar.append(pinned, wrap, updating);

  const detail = document.createElement("div");
  detail.className = "wc-detail";
  detail.setAttribute("role", "region");

  return { header, bar, pinned, scroller, prev, next, detail, updating };
}

/**
 * Re-render all cards: live/HT go to the fixed pinned area, everything else to
 * the scrolling area.
 */
export function fillCards(
  refs: BarRefs,
  matches: TickerMatch[],
  cfg: WidgetConfig
): void {
  refs.pinned.textContent = "";
  refs.scroller.textContent = "";
  for (const m of matches) {
    const card = createCard(m, cfg);
    if (m.status === "LIVE" || m.status === "HT") refs.pinned.appendChild(card);
    else refs.scroller.appendChild(card);
  }
}
